/**
 * Playing with the cousins: the moves one company makes towards another in an online season.
 * Each offer becomes a choice in the other player's studio; they answer it in their next quarter. If they never do,
 * the safe answer is taken (they keep their staff; deals they didn't agree to don't happen).
 *
 * - Poaching: offer one of their senior staff more money; they get one chance to match it.
 * - Co-publishing: a partner pays part of a game's first print run and takes the same share of its sales.
 * - Licences: use a cousin's hit theme for your next game and pay them a royalty on its sales.
 * - Print slots: a company with its own print shop sells spare capacity at a price per copy.
 * - Dicefall Con: the crowd award is voted by the players; nobody can vote for their own game.
 */
import { C } from "@bd/content";
import { WEEKS_PER_YEAR, yearOf } from "./calendar";
import { addDecision, type DecisionHandler } from "./company";
import type { CommandResult } from "./commands";
import type { Company, GameState, Product } from "./state";
import { clamp, digits, log, money } from "./util";

export type CousinCommand =
  | { type: "cousinPoach"; companyId: string; staffId: string; offer: number }
  | { type: "coPublish"; companyId: string; projectId: string; share: number }
  | { type: "licenceCousin"; companyId: string; productId: string; royalty: number }
  | { type: "sellPrintSlots"; companyId: string; copies: number; unitPrice: number }
  | { type: "crowdVote"; productId: string; companyId: string };

/** Offers wait half a year for an answer: the other player gets at least one whole quarter to see them. */
const ANSWER_WEEKS = 26;
const fail = (error: string): CommandResult => ({ ok: false, error });

function cousin(state: GameState, co: Company, id: string): Company | null {
  const c = state.companies.find((x) => x.id === id);
  return c && c.id !== co.id && !c.bankrupt ? c : null;
}

export function applyCousin(state: GameState, co: Company, cmd: CousinCommand): CommandResult {
  if (state.companies.length < 2) return fail("That's for seasons with the cousins.");
  const other = cousin(state, co, cmd.companyId);
  if (!other) return fail("That company isn't in this season.");
  switch (cmd.type) {
    case "cousinPoach": {
      const s = other.staff.find((x) => x.id === cmd.staffId);
      if (!s || s.founder) return fail("They can't be hired away.");
      if (s.level < 2) return fail(`${s.name} isn't senior enough to poach yet.`);
      if (!(cmd.offer >= s.salary * 1.2)) return fail(`Offer at least ${money(s.salary * 1.2)} a week (20% more than now).`);
      if (co.staff.length >= C.level[co.level].staffCap) return fail("Your studio is full. Move to a bigger place first.");
      if (co.cash < cmd.offer * 8) return fail(`Keep at least ${money(cmd.offer * 8)} in the bank to afford them.`);
      if (other.pending.some((d) => d.kind === "cousinPoach" && d.data?.staffId === s.id)) return fail(`Someone has already made ${s.name} an offer.`);
      addDecision(state, other, {
        kind: "cousinPoach", title: `${co.name} wants ${s.name}`,
        text: `${co.name} offers ${s.name} ${money(cmd.offer)} a week. Match it to keep them, or let them go.`,
        options: [{ id: "match", label: `Match: ${money(cmd.offer)} a week` }, { id: "go", label: "Let them go" }],
        defaultOption: "match", weeks: ANSWER_WEEKS, data: { from: co.id, staffId: s.id, offer: Math.round(cmd.offer) },
      });
      log(state, "cousin", `You offered ${s.name} ${money(cmd.offer)} a week to join you. ${other.name} can match it.`, co.id);
      return { ok: true };
    }
    case "coPublish": {
      const p = co.projects.find((x) => x.id === cmd.projectId);
      if (!p) return fail("No such project.");
      if (!(cmd.share >= 0.2 && cmd.share <= 0.5)) return fail("Offer a share between 20% and 50%.");
      if (p.partner) return fail("This game already has a partner.");
      if (other.pending.some((d) => d.kind === "coPublish" && d.data?.projectId === p.id)) return fail("You've already offered this one.");
      const pct = Math.round(cmd.share * 100);
      addDecision(state, other, {
        kind: "coPublish", title: `Co-publish ${p.design.name}?`,
        text: `${co.name} offers you ${pct}% of ${p.design.name}: you pay ${pct}% of its first print run and get ${pct}% of every sale.`,
        options: [{ id: "accept", label: `Take ${pct}%` }, { id: "decline", label: "No thanks" }],
        defaultOption: "decline", weeks: ANSWER_WEEKS, data: { from: co.id, projectId: p.id, share: cmd.share },
      });
      log(state, "cousin", `You offered ${other.name} ${pct}% of ${p.design.name}.`, co.id);
      return { ok: true };
    }
    case "licenceCousin": {
      const hit = other.products.find((x) => x.id === cmd.productId && x.launched);
      if (!hit) return fail("No such game.");
      if (hit.score < 7.5) return fail("Only hits (7.5 or more) are worth licensing.");
      if (!(cmd.royalty >= 0.05 && cmd.royalty <= 0.25)) return fail("Offer a royalty between 5% and 25%.");
      if ((co.cousinLicences ?? []).some((l) => l.theme === hit.design.theme)) return fail("You already have that theme licensed.");
      const pct = Math.round(cmd.royalty * 100);
      const theme = C.theme[hit.design.theme].name;
      addDecision(state, other, {
        kind: "licenceCousin", title: `${co.name} wants to license ${hit.design.name}`,
        text: `${co.name} wants to make a ${theme.toLowerCase()} game in the world of ${hit.design.name}, and pay you ${pct}% of its sales.`,
        options: [{ id: "accept", label: `Agree: ${pct}% royalty` }, { id: "decline", label: "No thanks" }],
        defaultOption: "decline", weeks: ANSWER_WEEKS, data: { from: co.id, productId: hit.id, royalty: cmd.royalty },
      });
      log(state, "cousin", `You asked ${other.name} to license ${hit.design.name} for ${pct}% of sales.`, co.id);
      return { ok: true };
    }
    case "sellPrintSlots": {
      if (!co.rooms.some((r) => r.type === "print_shop")) return fail("You need your own print shop to sell print slots.");
      if (!(cmd.copies >= 500 && cmd.copies <= 50000)) return fail("Offer between 500 and 50,000 copies.");
      if (!(cmd.unitPrice > 0)) return fail("Set a price per copy.");
      addDecision(state, other, {
        kind: "printSlots", title: `Print slots from ${co.name}`,
        text: `${co.name} can print ${digits(cmd.copies)} copies for you at ${money(cmd.unitPrice)} each, any time in the next year. You pay when you print.`,
        options: [{ id: "accept", label: "Book them" }, { id: "decline", label: "No thanks" }],
        defaultOption: "decline", weeks: ANSWER_WEEKS, data: { from: co.id, copies: Math.round(cmd.copies), unitPrice: cmd.unitPrice },
      });
      log(state, "cousin", `You offered ${other.name} ${digits(cmd.copies)} print slots at ${money(cmd.unitPrice)} a copy.`, co.id);
      return { ok: true };
    }
    case "crowdVote": {
      const p = other.products.find((x) => x.id === cmd.productId && x.launched);
      if (!p) return fail("No such game.");
      if (state.week - p.releaseWeek > WEEKS_PER_YEAR) return fail("Only this year's games are up for the crowd award.");
      const year = String(yearOf(state.week));
      (state.votes ??= {})[year] ??= {};
      state.votes[year][co.id] = p.marketId;
      log(state, "cousin", `Your crowd vote for Dicefall Con: ${p.design.name}.`, co.id);
      return { ok: true };
    }
  }
}

function from(state: GameState, id: string | undefined): Company | undefined {
  return state.companies.find((c) => c.id === id);
}

/** Answers to cousins' offers (company.ts looks these up when a choice is answered). */
export const COUSIN_HANDLERS: Record<string, DecisionHandler> = {};

COUSIN_HANDLERS.cousinPoach = (state, co, d, option) => {
  const s = co.staff.find((x) => x.id === d.data?.staffId);
  const buyer = from(state, d.data?.from);
  if (!s || !buyer) return;
  const offer = d.data!.offer as number;
  if (option === "match") {
    s.salary = offer;
    s.morale = clamp(s.morale + 10, 0, 100);
    log(state, "cousin", `${s.name} stays with you for ${money(offer)} a week.`, co.id);
    log(state, "cousin", `${co.name} matched your offer: ${s.name} stays with them.`, buyer.id);
    return;
  }
  if (buyer.staff.length >= C.level[buyer.level].staffCap) {
    log(state, "cousin", `${s.name} wanted to join ${buyer.name}, but there was no room. They stay.`, co.id);
    return;
  }
  co.staff = co.staff.filter((x) => x.id !== s.id);
  buyer.staff.push({ ...s, salary: offer, projectId: undefined, training: undefined, crunch: false, hiredWeek: state.week, revealed: s.traits });
  log(state, "cousin", `${s.name} leaves for ${buyer.name}.`, co.id);
  log(state, "cousin", `${s.name} joins you from ${co.name} for ${money(offer)} a week.`, buyer.id);
};

COUSIN_HANDLERS.coPublish = (state, co, d, option) => {
  const owner = from(state, d.data?.from);
  const p = owner?.projects.find((x) => x.id === d.data?.projectId);
  if (!owner) return;
  if (option !== "accept" || !p || p.partner) {
    log(state, "cousin", `${co.name} said no to co-publishing.`, owner.id);
    return;
  }
  p.partner = { companyId: co.id, share: d.data!.share };
  log(state, "cousin", `${co.name} will co-publish ${p.design.name} with you.`, owner.id);
  log(state, "cousin", `You're co-publishing ${p.design.name} with ${owner.name}: you pay your share when it's printed.`, co.id);
};

COUSIN_HANDLERS.licenceCousin = (state, co, d, option) => {
  const buyer = from(state, d.data?.from);
  const hit = co.products.find((x) => x.id === d.data?.productId);
  if (!buyer || !hit) return;
  if (option !== "accept") {
    log(state, "cousin", `${co.name} said no to licensing ${hit.design.name}.`, buyer.id);
    return;
  }
  (buyer.cousinLicences ??= []).push({ from: co.id, productId: hit.id, name: hit.design.name, theme: hit.design.theme, royalty: d.data!.royalty, until: state.week + 2 * WEEKS_PER_YEAR });
  log(state, "cousin", `${co.name} agreed: your next ${C.theme[hit.design.theme].name.toLowerCase()} game can use the world of ${hit.design.name}.`, buyer.id);
};

COUSIN_HANDLERS.printSlots = (state, co, d, option) => {
  const seller = from(state, d.data?.from);
  if (!seller) return;
  if (option !== "accept") {
    log(state, "cousin", `${co.name} didn't need your print slots.`, seller.id);
    return;
  }
  (co.printCredits ??= []).push({ from: seller.id, copies: d.data!.copies, unitCost: d.data!.unitPrice, until: state.week + WEEKS_PER_YEAR });
  log(state, "cousin", `${co.name} booked your print slots.`, seller.id);
};

/** A new game in a licensed theme carries the licence: a little hype from the hit, a royalty to its owner. */
export function useCousinLicence(state: GameState, co: Company, prod: Product): void {
  const i = (co.cousinLicences ?? []).findIndex((l) => l.theme === prod.design.theme && l.until >= state.week);
  if (i < 0) return;
  const l = co.cousinLicences![i];
  co.cousinLicences!.splice(i, 1);
  (prod.shares ??= []).push({ companyId: l.from, share: l.royalty, why: "licence" });
  prod.hype = clamp(prod.hype + 0.15);
  log(state, "cousin", `${prod.design.name} is set in the world of ${l.name}.`, co.id);
}

/** What printing at a cousin's shop costs, or why you can't. */
export function cousinPrint(co: Company, sellerId: string, copies: number, week: number): { unitCost: number; total: number } | string {
  const c = (co.printCredits ?? []).find((x) => x.from === sellerId && x.until >= week);
  if (!c) return "You have no print slots booked there.";
  if (copies > c.copies) return `You have ${digits(c.copies)} slots left there.`;
  return { unitCost: c.unitCost, total: Math.round(c.unitCost * copies) };
}

/** Uses up the slots and pays the cousin who printed it. */
export function payCousinPrint(state: GameState, co: Company, sellerId: string, copies: number, total: number): void {
  const c = (co.printCredits ?? []).find((x) => x.from === sellerId && x.until >= state.week)!;
  c.copies -= copies;
  co.printCredits = (co.printCredits ?? []).filter((x) => x.copies > 0 && x.until >= state.week);
  const seller = from(state, sellerId);
  if (seller) {
    seller.shareIn = (seller.shareIn ?? 0) + total;
    log(state, "cousin", `${co.name} printed ${digits(copies)} copies at your shop: ${money(total)}.`, seller.id);
  }
}

/** Every sale of a shared game pays the partner's share (co-publishing or a licence). Returns what was paid out. */
export function payShares(state: GameState, prod: Product, income: number): number {
  let out = 0;
  for (const s of prod.shares ?? []) {
    const to = from(state, s.companyId);
    if (!to || income <= 0) continue;
    const pay = income * s.share;
    to.shareIn = (to.shareIn ?? 0) + pay;
    out += pay;
  }
  return out;
}

/** Players' votes for this year's crowd award: market game id -> votes. */
export function crowdVotes(state: GameState): Map<string, number> {
  const out = new Map<string, number>();
  for (const id of Object.values(state.votes?.[String(yearOf(state.week))] ?? {})) out.set(id, (out.get(id) ?? 0) + 1);
  return out;
}
